import React, { useEffect, useMemo, useState } from 'react'
import { Spin } from 'antd'
import Button from 'components/Button'
import usePdfPages from 'components/ResumePreview/usePdfPages'
import { fetchUploadedCv } from 'components/ResumePreview/api'
import {
  toDocumentViewerUrl,
  toGoogleEmbedUrl,
  useUploadKind,
} from 'components/ResumePreview/source'
import {
  FallbackCard,
  FallbackText,
  FallbackTitle,
  PageImage,
  PreviewFrame,
  PreviewWrapper,
  ScrollArea,
} from 'components/ResumePreview/style'

// Mirrors the rule student-node applies to the same row: a role mapping only
// carries a candidate-uploaded CV when isSystemResume is false AND a URL is
// actually present. Older Tally intake rows carry isSystemResume=false with
// cvUrl=null, so both halves are required. Returns the URL or null so callers
// can read it as "does this candidate have an uploaded resume for this role".
export const getUploadedCvUrl = candidate =>
  candidate?.isSystemResume === false && candidate?.cvUrl ? candidate.cvUrl : null

// Two shapes reach this component. A role mapping stores cvUrl as a plain
// string (student.student_role_mapping."cvUrl"); the student record stores it
// as jsonb — { url, name, size }. Normalising here keeps every call site from
// repeating the same ternary.
const toUrl = cvUrl => (typeof cvUrl === 'object' ? cvUrl?.url : cvUrl) || null

/**
 * Resume viewer — renders the CV the candidate uploaded, whatever it is.
 *
 * Only the uploaded CV is shown. The generated system resume is deliberately
 * not used as a fallback: recruiters asked to see what the candidate actually
 * submitted, so a candidate with no upload gets "No resume found" rather than a
 * document that looks like theirs but was built by us.
 *
 * The CV is found in two steps. A `cvUrl` handed down by the screen wins — it
 * is already role-scoped and costs no round trip. Otherwise student-node
 * resolves it (`GET /students/:id/uploaded-cv`), which is the only thing that
 * sees all three places a CV can live: the role mapping, the raw ingestion
 * response, and the student profile. That fallback matters more than it sounds:
 * the drive candidate list never selected cvUrl at all, so those drawers had
 * nothing to hand down, and 762 role mappings on UAT hold the candidate's only
 * CV link inside the ingestion response.
 *
 * Every link previews. PDFs are rasterised here; photographed resumes render as
 * images; Word/PowerPoint files and Google Drive links go through Google's
 * viewers, which is the only way to draw those in a browser. A PDF that fails
 * to parse is handed to the document viewer as a second attempt before the
 * drawer admits defeat — see `source.js` for the shape of the data behind this.
 */
const ResumePreview = ({ cvUrl, studentId, roleId, candidateName = 'This candidate' }) => {
  const propUrl = useMemo(() => toUrl(cvUrl), [cvUrl])
  const [resolvedUrl, setResolvedUrl] = useState(null)
  const [isResolving, setIsResolving] = useState(false)
  const [hasImageFailed, setHasImageFailed] = useState(false)

  useEffect(() => {
    if (propUrl || !studentId) {
      setResolvedUrl(null)
      return undefined
    }

    let cancelled = false
    setIsResolving(true)
    fetchUploadedCv(studentId, roleId)
      .then(url => {
        if (!cancelled) setResolvedUrl(url)
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false)
      })

    return () => {
      cancelled = true
    }
  }, [propUrl, studentId, roleId])

  const uploadUrl = propUrl || resolvedUrl
  const { kind, isSniffing } = useUploadKind(uploadUrl)

  useEffect(() => setHasImageFailed(false), [uploadUrl])

  const { pages, isRendering, error } = usePdfPages(kind === 'pdf' ? uploadUrl : null)
  // A key that pdfjs could not parse is nearly always a document saved under
  // the wrong extension, so retry through the viewer that sniffs the file
  // rather than telling the recruiter it cannot be shown.
  const usesDocumentViewer = kind === 'document' || (kind === 'pdf' && error)

  const openUpload = () => window.open(uploadUrl, '_blank', 'noopener,noreferrer')

  const body = () => {
    if (isResolving || isSniffing || isRendering) {
      return (
        <FallbackCard>
          <Spin />
          <FallbackText>Preparing the resume…</FallbackText>
        </FallbackCard>
      )
    }

    if (!uploadUrl) {
      return (
        <FallbackCard>
          <FallbackTitle>No resume found</FallbackTitle>
          <FallbackText>{candidateName} has not uploaded a resume.</FallbackText>
        </FallbackCard>
      )
    }

    if (pages.length) {
      return (
        <ScrollArea>
          {pages.map((page, index) => (
            <PageImage
              // Page order is the identity here — there is nothing else stable.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              src={page}
              alt={`${candidateName} resume, page ${index + 1}`}
            />
          ))}
        </ScrollArea>
      )
    }

    if (kind === 'image' && !hasImageFailed) {
      return (
        <ScrollArea>
          <PageImage
            src={uploadUrl}
            alt={`${candidateName} resume`}
            onError={() => setHasImageFailed(true)}
          />
        </ScrollArea>
      )
    }

    // A Google link with no file id in it — a folder, or a share URL we do not
    // recognise — has nothing to embed, so it drops to the card below.
    const frameUrl = kind === 'google' ? toGoogleEmbedUrl(uploadUrl) : usesDocumentViewer && toDocumentViewerUrl(uploadUrl)
    if (frameUrl) {
      return <PreviewFrame src={frameUrl} title={`${candidateName} resume`} />
    }

    // Nothing left to try — keep the original one click away.
    return (
      <FallbackCard>
        <FallbackTitle>Unable to display this resume</FallbackTitle>
        <FallbackText>
          {`${candidateName}'s uploaded resume could not be opened here.`}
        </FallbackText>
        <Button.Primary
          height="38px"
          width="200px"
          text="Open uploaded resume"
          onClick={openUpload}
        />
      </FallbackCard>
    )
  }

  return <PreviewWrapper>{body()}</PreviewWrapper>
}

export default ResumePreview
